import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

export function usePipelineData({ api, emptyLeadMeta, pageSize, calcScore, selected, setSelected }) {
  const [leads, setLeads] = useState([]);
  const [leadMeta, setLeadMeta] = useState(emptyLeadMeta);

  const [pipeFilter, setPipeFilter] = useState("All Statuses");
  const [pipeSource, setPipeSource] = useState("All Sources");
  const [pipeSearch, setPipeSearch] = useState("");
  const [pipeQuickFilter, setPipeQuickFilter] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [detailTab, setDetailTab] = useState("info");

  const [pipelineTotal, setPipelineTotal] = useState(0);
  const [pipelineOffset, setPipelineOffset] = useState(0);
  const [pipelineHasMore, setPipelineHasMore] = useState(false);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [debouncedPipeSearch, setDebouncedPipeSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPipeSearch(pipeSearch.trim()), 250);
    return () => clearTimeout(timer);
  }, [pipeSearch]);

  const loadLeadMeta = useCallback(async () => {
    try {
      const response = await fetch(`${api}/api/leads/meta`);
      const data = await response.json();
      if (response.ok && data.ok && data.meta) {
        setLeadMeta({ ...emptyLeadMeta, ...data.meta });
      }
    } catch {}
  }, [api, emptyLeadMeta]);

  const buildLeadQuery = useCallback((offset = 0, limit = pageSize) => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });

    if (pipeFilter !== "All Statuses") params.set("status", pipeFilter);
    if (pipeSource !== "All Sources") params.set("source", pipeSource);
    if (pipeQuickFilter) params.set("quickFilter", pipeQuickFilter);
    if (debouncedPipeSearch) params.set("q", debouncedPipeSearch);

    return params.toString();
  }, [pageSize, pipeFilter, pipeSource, pipeQuickFilter, debouncedPipeSearch]);

  const fetchPipelinePage = useCallback(async (offset = 0, reset = false) => {
    setPipelineLoading(true);
    try {
      const response = await fetch(`${api}/api/leads?${buildLeadQuery(offset)}`);
      const data = await response.json();
      if (!response.ok || !data.ok) return;

      const nextLeads = (data.leads || []).map((lead) => ({ ...lead, score: calcScore(lead) }));

      setLeads((prev) => {
        if (reset) return nextLeads;
        const existingIds = new Set(prev.map((lead) => lead.id));
        return [...prev, ...nextLeads.filter((lead) => !existingIds.has(lead.id))];
      });
      setPipelineTotal(Number(data.total || 0));
      setPipelineOffset(offset + nextLeads.length);
      setPipelineHasMore(Boolean(data.hasMore));
    } catch {
    } finally {
      setPipelineLoading(false);
    }
  }, [api, buildLeadQuery, calcScore]);

  const loadMorePipeline = useCallback(() => {
    if (pipelineLoading || !pipelineHasMore) return;
    fetchPipelinePage(pipelineOffset, false);
  }, [pipelineLoading, pipelineHasMore, fetchPipelinePage, pipelineOffset]);

  const refreshPipeline = useCallback(async () => {
    setSelected(null);
    setPipelineOffset(0);
    setPipelineHasMore(false);
    await fetchPipelinePage(0, true);
  }, [fetchPipelinePage, setSelected]);

  useEffect(() => {
    loadLeadMeta();
  }, [loadLeadMeta]);

  useEffect(() => {
    refreshPipeline();
  }, [refreshPipeline]);

  const saveLeads = useCallback(async (newLeads) => {
    try {
      const response = await fetch(`${api}/api/leads/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leads: newLeads }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        return { ok: false, error: data.error || "Failed to save leads" };
      }
      return {
        ok: true,
        inserted: Number(data.inserted || 0),
        total: Number(data.total || newLeads.length),
      };
    } catch (error) {
      return { ok: false, error: error?.message || "Failed to save leads" };
    }
  }, [api]);

  const updateLead = useCallback(async (id, changes) => {
    const scoreFields = ["website", "websiteStatus", "phone", "email", "employees", "industry"];
    const currentLead = leads.find((lead) => lead.id === id);
    if (!currentLead) return;

    const merged = { ...currentLead, ...changes };
    const needsRescore = scoreFields.some((field) => field in changes);

    if (needsRescore) {
      merged.score = calcScore(merged);
    }

    setLeads((prev) => prev.map((lead) => lead.id === id ? merged : lead));
    if (selected?.id === id) setSelected(merged);

    try {
      await fetch(`${api}/api/leads/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(needsRescore ? { ...changes, score: merged.score } : changes),
      });
      if (["status", "decision", "website", "email", "score"].some((field) => field in changes)) {
        loadLeadMeta();
      }
    } catch {}
  }, [api, calcScore, leads, loadLeadMeta, selected, setSelected]);

  const deleteLead = useCallback(async (id) => {
    setLeads((prev) => prev.filter((lead) => lead.id !== id));
    if (selected?.id === id) setSelected(null);
    try {
      await fetch(`${api}/api/leads/${id}`, { method: "DELETE" });
      loadLeadMeta();
      refreshPipeline();
    } catch {}
  }, [api, loadLeadMeta, refreshPipeline, selected, setSelected]);

  const clearAllLeads = useCallback(async () => {
    setLeads([]);
    setSelected(null);
    setPipelineTotal(0);
    setPipelineOffset(0);
    setPipelineHasMore(false);
    setLeadMeta(emptyLeadMeta);
    try {
      await fetch(`${api}/api/leads`, { method: "DELETE" });
    } catch {}
    toast.success("All leads cleared");
  }, [api, emptyLeadMeta, setSelected]);

  return {
    leads,
    setLeads,
    leadMeta,
    setLeadMeta,
    pipeFilter,
    setPipeFilter,
    pipeSource,
    setPipeSource,
    pipeSearch,
    setPipeSearch,
    pipeQuickFilter,
    setPipeQuickFilter,
    viewMode,
    setViewMode,
    detailTab,
    setDetailTab,
    pipelineTotal,
    pipelineOffset,
    setPipelineOffset,
    pipelineHasMore,
    setPipelineHasMore,
    pipelineLoading,
    loadLeadMeta,
    loadMorePipeline,
    refreshPipeline,
    saveLeads,
    updateLead,
    deleteLead,
    clearAllLeads,
  };
}
